import * as Sentry from '@sentry/react';
import React, { useState, useMemo, useEffect } from 'react';
import { X, Loader as Loader2, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Package, MapPin, Truck, Printer, ExternalLink, Copy, Check, TriangleAlert as AlertTriangle, CreditCard, User, Globe, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import type { ShopifyOrder, FinishOrderResponse } from '../../types/shopify';
import { formatCurrency } from '../../types/shopify';
import { supabase } from '../../lib/supabase';
import { classifyOrderIssue } from '../../utils/classifyOrderIssue';
import { checkShopifyOrderLiveStatus } from '../../utils/shopifyOrderLiveStatus';
import { useAiAddress, type AiNormalizeResult } from '../../hooks/useAiAddress';

interface FinishOrderModalProps {
  order: ShopifyOrder;
  onClose: () => void;
  onFinish: (orderId: number) => Promise<FinishOrderResponse>;
  adminId?: string | null;
}

type ModalState = 'confirm' | 'processing' | 'success' | 'error';

interface AddressCheck {
  label: string;
  value: string | null;
  valid: boolean;
  warning?: string;
}

const COUNTRY_NAMES: Record<string, string> = {
  NL: 'Netherlands', BE: 'Belgium', DE: 'Germany', FR: 'France', GB: 'United Kingdom',
  ES: 'Spain', IT: 'Italy', PT: 'Portugal', AT: 'Austria', CH: 'Switzerland',
  PL: 'Poland', SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland',
  IE: 'Ireland', GR: 'Greece', CZ: 'Czech Republic', US: 'United States',
};

const COUNTRIES_WITHOUT_POSTAL_CODES = new Set([
  'AE', 'HK', 'QA', 'YE', 'KP', 'MO', 'AO', 'BW', 'GH', 'UG',
  'CM', 'BI', 'BJ', 'BS', 'BZ', 'AW', 'CW', 'BO', 'FJ', 'TO', 'TV',
]);

const FLAG_EMOJIS: Record<string, string> = {
  NL: '🇳🇱', BE: '🇧🇪', DE: '🇩🇪', FR: '🇫🇷', GB: '🇬🇧', ES: '🇪🇸', IT: '🇮🇹',
  PT: '🇵🇹', AT: '🇦🇹', CH: '🇨🇭', PL: '🇵🇱', SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰',
  FI: '🇫🇮', IE: '🇮🇪', GR: '🇬🇷', CZ: '🇨🇿', US: '🇺🇸',
};

const getDestinationType = (countryCode: string): { type: string; color: string } => {
  const cc = (countryCode || 'NL').toUpperCase();
  if (cc === 'NL') return { type: 'Domestic', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
  if (cc === 'BE') return { type: 'Belgium', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' };
  const EU = ['AT', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];
  if (EU.includes(cc)) return { type: 'EU', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
  return { type: 'International', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' };
};

const FinishOrderModal: React.FC<FinishOrderModalProps> = ({ order, onClose, onFinish, adminId }) => {
  const [state, setState] = useState<ModalState>('confirm');
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<FinishOrderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { processing: aiProcessing, lastResult: aiResult, aiError, normalizeAddress } = useAiAddress();

  const address = order.shipping_address;
  const countryCode = (address?.country_code || address?.country || 'NL').toUpperCase().replace('UK', 'GB');
  const destination = getDestinationType(countryCode);

  const addressChecks = useMemo((): AddressCheck[] => {
    const checks: AddressCheck[] = [];

    let name = '';
    if (address?.name) name = address.name;
    else if (address?.company) name = address.company;
    checks.push({ label: 'Recipient', value: name || null, valid: !!name });

    checks.push({ label: 'Street', value: address?.address1 || null, valid: !!address?.address1 });
    checks.push({ label: 'City', value: address?.city || null, valid: !!address?.city });
    const postalCodeRequired = !COUNTRIES_WITHOUT_POSTAL_CODES.has(countryCode);
    checks.push({ label: 'Postal Code', value: address?.zip || null, valid: !!address?.zip || !postalCodeRequired });
    checks.push({
      label: 'Country',
      value: COUNTRY_NAMES[countryCode] || countryCode,
      valid: true
    });

    return checks;
  }, [address, countryCode]);

  const addressValid = addressChecks.every(c => c.valid);

  const isPartiallyRefunded = order.financial_status === 'PARTIALLY_REFUNDED';
  const paymentAcceptable = order.financial_status === 'PAID' || isPartiallyRefunded;

  const preflightChecks = useMemo(() => [
    { label: 'Order is ready', passed: order.is_ready, icon: CheckCircle, warning: false },
    {
      label: isPartiallyRefunded ? 'Payment: Partially Refunded' : 'Payment confirmed',
      passed: paymentAcceptable,
      icon: CreditCard,
      warning: isPartiallyRefunded,
    },
    { label: 'Shipping address valid', passed: addressValid, icon: MapPin, warning: false },
    { label: 'Not yet fulfilled', passed: order.fulfillment_status !== 'FULFILLED' && !order.fulfilled_at, icon: Package, warning: false },
  ], [order, addressValid, isPartiallyRefunded, paymentAcceptable]);

  const allChecksPassed = preflightChecks.every(c => c.passed);
  
  // Defensive check: prevent finishing already-fulfilled orders
  const isAlreadyFinished = order.fulfillment_status === 'FULFILLED' || !!order.fulfilled_at;

  useEffect(() => {
    if (address) {
      normalizeAddress({
        name: address.name || '',
        street: address.address1 || '',
        addressExtra: address.address2 || '',
        postalCode: address.zip || '',
        city: address.city || '',
        countryCode,
        region: address.province || address.province_code || '',
        phone: address.phone || '',
      }, undefined, {
        shopifyOrderId: order.shopify_order_id,
        orderNumber: order.order_number || undefined,
        source: 'finish_order',
      }).then((result) => {
        if (!result) return;
        const actionableIssues = result.diagnostics?.issues?.filter(i => i.severity === 'error' || i.severity === 'warning') || [];
        if (actionableIssues.length > 0 && adminId && order.order_number) {
          const issueDesc = 'AI detected: ' + actionableIssues.map(i => `${i.field}: ${i.message}`).join('; ');
          supabase.rpc('upsert_order_issue', {
            p_admin_id: adminId,
            p_order_reference: order.order_number,
            p_shopify_order_id: order.shopify_order_id ?? null,
            p_client_name: order.customer_name || null,
            p_recipient_address: address ?? {},
            p_issue_type: actionableIssues.some(i => i.severity === 'error') ? 'address_validation_error' : 'incomplete_address',
            p_issue_description: issueDesc,
            p_error_code: null,
            p_source: 'ai_address_validation',
          }).then(() => {}, () => {});
        }
      });
    }
  }, [order.shopify_order_id]);

  const handleFinish = async () => {
    // Double-check that order is not already finished
    if (isAlreadyFinished) {
      setError('This order has already been finished and cannot be processed again.');
      setState('error');
      return;
    }

    setState('processing');
    setProgress('Checking live order status...');
    setError(null);

    try {
      const liveStatus = await checkShopifyOrderLiveStatus(order.shopify_order_id);
      if (liveStatus.error) {
        throw new Error(liveStatus.error);
      }
      if (liveStatus.cancelled) {
        setError(`Order ${order.order_number} was cancelled in Shopify and removed from the queue.`);
        setState('error');
        window.setTimeout(() => onClose(), 2500);
        return;
      }

      setProgress('Validating order...');
      await new Promise(r => setTimeout(r, 300));

      setProgress('Generating PostNL label...');
      await new Promise(r => setTimeout(r, 500));

      setProgress('Creating Shopify fulfillment...');
      const response = await onFinish(order.shopify_order_id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to finish order');
      }

      setProgress('Pushing tracking info...');
      await new Promise(r => setTimeout(r, 300));

      setResult(response);
      setState('success');

      if (adminId && order.order_number) {
        try {
          await supabase.rpc('resolve_order_issue_by_reference', {
            p_admin_id: adminId,
            p_order_reference: order.order_number,
          });
        } catch (resolveErr) {
          Sentry.captureException(resolveErr, { tags: { operation: 'resolve_order_issue' }, extra: { orderNumber: order.order_number } });
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMsg);
      setState('error');

      Sentry.addBreadcrumb({ category: 'finish_order', message: `Order ${order.order_number} failed: ${errorMsg}`, level: 'error' });

      if (adminId && order.order_number) {
        try {
          await supabase.rpc('upsert_order_issue', {
            p_admin_id: adminId,
            p_order_reference: order.order_number,
            p_shopify_order_id: order.shopify_order_id ?? null,
            p_client_name: order.customer_name ?? null,
            p_recipient_address: order.shipping_address ?? {},
            p_issue_type: classifyOrderIssue(errorMsg),
            p_issue_description: errorMsg,
            p_error_code: null,
            p_source: 'finish_order_modal',
          });
        } catch (upsertErr) {
          console.error('Failed to record order issue:', upsertErr);
          Sentry.captureException(upsertErr, { tags: { operation: 'upsert_order_issue' }, extra: { orderNumber: order.order_number, errorMsg } });
        }
      }
    }
  };

  const handlePrintLabel = () => {
    if (!result?.labelPdf) return;
    const blob = new Blob([Uint8Array.from(atob(result.labelPdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleCopyTracking = () => {
    if (!result?.trackingCode) return;
    navigator.clipboard.writeText(result.trackingCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Finish Order {order.order_number}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span>{order.customer_name || 'Unknown'}</span>
                <span className="text-lg">{FLAG_EMOJIS[countryCode] || ''}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${destination.color}`}>
                  {destination.type}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5">
          {state === 'confirm' && (
            <>
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Pre-flight Checks
                </h3>
                <div className="space-y-2">
                  {preflightChecks.map((check, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        check.passed && check.warning
                          ? 'bg-amber-50 dark:bg-amber-900/20'
                          : check.passed
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'bg-red-50 dark:bg-red-900/20'
                      }`}
                    >
                      {check.passed && check.warning ? (
                        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      ) : check.passed ? (
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                      )}
                      <span className={`text-sm font-medium ${
                        check.passed && check.warning
                          ? 'text-amber-700 dark:text-amber-300'
                          : check.passed
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-red-700 dark:text-red-300'
                      }`}>
                        {check.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Shipping Address
                </h3>
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 space-y-2">
                  {addressChecks.map((check, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{check.label}</span>
                      <span className={`text-sm font-medium ${
                        check.valid
                          ? 'text-gray-900 dark:text-gray-100'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {check.value || 'Missing'}
                      </span>
                    </div>
                  ))}
                </div>
                {!addressValid && (
                  <div className="mt-2 flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Some address fields are missing. Label generation may fail.</span>
                  </div>
                )}
                {aiProcessing && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-violet-600 dark:text-violet-400">
                    <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                    <span>AI validating address…</span>
                  </div>
                )}
                {aiResult?.diagnostics?.issues && aiResult.diagnostics.issues.length > 0 && (
                  <div className="mt-2 p-3 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-violet-700 dark:text-violet-300 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-violet-900 dark:text-violet-200">AI Address Diagnostics:</p>
                        <ul className="mt-1 list-disc list-inside text-xs text-violet-800 dark:text-violet-300 space-y-0.5">
                          {aiResult.diagnostics.issues.map((issue, idx) => (
                            <li key={`ai-issue-${idx}`} className={issue.severity === 'error' ? 'text-red-700 dark:text-red-300 font-medium' : ''}>
                              [{issue.severity}] {issue.field}: {issue.message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-5">
                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                  What will happen:
                </h4>
                <ol className="text-xs text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
                  <li>Generate PostNL shipping label</li>
                  <li>Create fulfillment in Shopify</li>
                  <li>Push tracking info to customer</li>
                  <li>Mark order as fulfilled</li>
                </ol>
              </div>

              <div className="flex gap-3">
                <Button variant="secondary" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleFinish}
                  disabled={!allChecksPassed || isAlreadyFinished}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  <Truck className="h-4 w-4 mr-2" />
                  Generate Label & Fulfill
                </Button>
              </div>
            </>
          )}

          {state === 'processing' && (
            <div className="py-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Processing Order
              </h3>
              <p className="text-gray-600 dark:text-gray-400">{progress}</p>
            </div>
          )}

          {state === 'success' && result && (
            <>
              <div className="py-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Order Finished Successfully
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {result.shopifyFulfilled
                    ? 'Label generated and order fulfilled in Shopify'
                    : 'Label generated (Shopify fulfillment may need manual action)'}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Tracking Code</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {result.trackingCode}
                    </span>
                    <button
                      onClick={handleCopyTracking}
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Carrier</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">PostNL</span>
                </div>
              </div>

              {result.fulfillmentError && (
                <div className="mb-5 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      Shopify Fulfillment Warning
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      {result.fulfillmentError}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handlePrintLabel}
                  className="flex-1"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print Label
                </Button>
                {result.trackingUrl && (
                  <a
                    href={result.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Track Package
                  </a>
                )}
              </div>

              <button
                onClick={onClose}
                className="w-full mt-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Close
              </button>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="py-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Failed to Finish Order
                </h3>
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>

              <div className="flex gap-3">
                <Button variant="secondary" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setState('confirm')}
                  className="flex-1"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinishOrderModal;
